import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { IoEyeOutline, IoEyeOffOutline, IoBriefcaseOutline, IoBusinessOutline, IoChatbubble, IoLogoApple } from 'react-icons/io5';
import { FcGoogle } from 'react-icons/fc';
import { login, signInWithSocial, findUserByAuth, authorizeKakao } from '../../services/authService';
import { useAuth } from '../../context/AuthContext';
import ChangePasswordModal from './ChangePasswordModal';
import AlertModal from '../../components/common/AlertModal';
import LoadingOverlay from '../../components/common/LoadingOverlay';

const isIOS = typeof navigator !== 'undefined' && /iPhone|iPad|iPod/i.test(navigator.userAgent);

const LoginPage = () => {
  const navigate = useNavigate();
  const { refreshSession } = useAuth();
  const isDev = process.env.NODE_ENV === 'development';
  const devDefaults = { id: 'realtor3@test.com', pw: 'test1234!' };
  const [loginId, setLoginId] = useState(isDev ? devDefaults.id : '');
  const [password, setPassword] = useState(isDev ? devDefaults.pw : '');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pwChangeUid, setPwChangeUid] = useState(null);
  const [alert, setAlert] = useState(null);
  const [socialLoading, setSocialLoading] = useState('');

  const handleSocial = async (provider) => {
    if (socialLoading) return;
    setSocialLoading(provider);
    try {
      // RN WebView 환경이 아닐 때만 카카오 웹 redirect 방식 사용
      const isApp = typeof window !== 'undefined' && !!window.ReactNativeWebView;
      if (provider === 'kakao' && !isApp) {
        authorizeKakao();
        return; // Kakao 웹은 redirect — 이후 코드는 /auth/kakao/callback 에서 처리
      }
      const { authUid, email, displayName } = await signInWithSocial(provider);
      const lookup = await findUserByAuth(authUid);

      if (lookup.found) {
        // 기존 계정 연결됨 — 로그인 완료
        const user = lookup.user;
        if (user.status === 'pending') {
          await (await import('firebase/auth')).signOut((await import('../../firebase/config')).auth);
          setAlert({
            type: 'info',
            title: '이음에서 승인을 검토 중입니다',
            message: '관리자 승인 후 로그인하실 수 있습니다.',
          });
          return;
        }
        if (user.status === 'suspended') {
          await (await import('firebase/auth')).signOut((await import('../../firebase/config')).auth);
          setAlert({
            type: 'error',
            title: '계정이 정지되었습니다',
            message: '관리자에게 문의하세요.',
          });
          return;
        }
        await refreshSession();
        navigate('/', { replace: true });
        return;
      }

      // 신규 소셜 로그인 → 전화번호 인증부터 (기존 계정 통합 우선 탐색)
      navigate('/social/link-phone', {
        state: { authUid, email, displayName, provider },
      });
    } catch (err) {
      if (err?.code === 'auth/popup-closed-by-user' || err?.code === 'auth/cancelled-popup-request') {
        return; // 사용자가 팝업 닫음 — 조용히 무시
      }
      setAlert({
        type: 'error',
        title: '로그인에 실패했습니다',
        message: err?.message || '잠시 후 다시 시도해 주세요.',
      });
    } finally {
      setSocialLoading('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(loginId.trim(), password);
      if (result?.tempPassword) {
        setPwChangeUid(result.uid);
        return;
      }
      await refreshSession();
      navigate('/', { replace: true });
    } catch (err) {
      const code = err.code || '';
      if (code === 'auth/pending') {
        setAlert({
          type: 'info',
          title: '이음에서 승인을 검토 중입니다',
          message: '관리자 승인 후 로그인하실 수 있습니다.\n자세한 문의는 고객센터로 연락주세요.',
        });
      } else if (code === 'auth/suspended') {
        setAlert({
          type: 'error',
          title: '계정이 정지되었습니다',
          message: '관리자에게 문의하세요.',
        });
      } else if (code === 'auth/too-many-requests') {
        setAlert({
          type: 'error',
          title: '시도가 너무 많습니다',
          message: '잠시 후 다시 시도해 주세요.',
        });
      } else {
        setAlert({
          type: 'error',
          title: '입력하신 정보가 일치하지 않습니다',
          message: '아이디가 존재하지 않거나 입력하신 정보를\n다시 한번 확인해 주시기 바랍니다',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Wrapper>
      {(loading || socialLoading) && <LoadingOverlay minimal message="로그인 중입니다..." />}
      <Inner>
        <TitleWrap>
          <PageTitle>로그인</PageTitle>
          <PageSub>권한이 부여된 계정으로 접속해 주세요.</PageSub>
        </TitleWrap>

        <form onSubmit={handleSubmit}>
          <Field>
            <Label>이메일</Label>
            <InputWrap>
              <Input
                type="text"
                placeholder="이메일을 입력해 주세요"
                value={loginId}
                onChange={(e) => setLoginId(e.target.value)}
                autoComplete="username"
                required
              />
            </InputWrap>
          </Field>

          <Field>
            <Label>비밀번호</Label>
            <InputWrap>
              <Input
                type={showPw ? 'text' : 'password'}
                placeholder="비밀번호를 입력해 주세요"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                $hasIcon
                required
              />
              <EyeButton
                type="button"
                onClick={() => setShowPw((v) => !v)}
                aria-label={showPw ? '비밀번호 숨기기' : '비밀번호 표시'}
              >
                {showPw ? <IoEyeOffOutline size={20} /> : <IoEyeOutline size={20} />}
              </EyeButton>
            </InputWrap>
          </Field>

          <ForgotLink to="/forgot-password">비밀번호 찾기</ForgotLink>

          <SubmitButton type="submit" disabled={loading}>
            {loading ? '로그인 중...' : '로그인'}
          </SubmitButton>
        </form>

        <Divider><span>또는</span></Divider>

        <SocialList>
          <SocialBtn
            type="button"
            $variant="kakao"
            disabled={!!socialLoading}
            onClick={() => handleSocial('kakao')}
          >
            <IoChatbubble size={18} />
            <span>{socialLoading === 'kakao' ? '진행 중...' : '카카오 로그인'}</span>
          </SocialBtn>
          <SocialBtn
            type="button"
            $variant="google"
            disabled={!!socialLoading}
            onClick={() => handleSocial('google')}
          >
            <FcGoogle size={18} />
            <span>{socialLoading === 'google' ? '진행 중...' : '구글 로그인'}</span>
          </SocialBtn>
          {isIOS && (
            <SocialBtn
              type="button"
              $variant="apple"
              disabled={!!socialLoading}
              onClick={() => handleSocial('apple')}
            >
              <IoLogoApple size={18} />
              <span>{socialLoading === 'apple' ? '진행 중...' : '애플 로그인'}</span>
            </SocialBtn>
          )}
        </SocialList>

        <SignupSection>
          <SignupTitle>회원가입/신청</SignupTitle>
          <SignupRow>
            <SignupBtn type="button" onClick={() => navigate('/partner/login')}>
              <IoBriefcaseOutline size={18} />
              <span>제휴업체</span>
            </SignupBtn>
            <SignupBtn type="button" onClick={() => navigate('/signup')}>
              <IoBusinessOutline size={18} />
              <span>공인중개사</span>
            </SignupBtn>
          </SignupRow>
        </SignupSection>

      </Inner>

      {pwChangeUid && (
        <ChangePasswordModal
          uid={pwChangeUid}
          onSuccess={async () => {
            await refreshSession();
            setPwChangeUid(null);
            navigate('/', { replace: true });
          }}
        />
      )}

      {alert && (
        <AlertModal
          type={alert.type}
          title={alert.title}
          message={alert.message}
          onConfirm={() => setAlert(null)}
        />
      )}
    </Wrapper>
  );
};

export default LoginPage;

// ───── 스타일 ─────
const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: ${({ theme }) => theme.spacing.lg};
  background: ${({ theme }) => theme.colors.bg};
`;

const Inner = styled.div`
  width: 100%;
  max-width: 400px;
  padding: 0 ${({ theme }) => theme.spacing.sm};
`;

const TitleWrap = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xxl};
  padding-top: ${({ theme }) => theme.spacing.sm};
`;

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${({ theme }) => theme.spacing.md};
`;

const PageSub = styled.p`
  font-size: ${({ theme }) => theme.fontSize.base};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
`;

const Field = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.base};
`;

const Label = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const InputWrap = styled.div`
  position: relative;
`;

const Input = styled.input`
  width: 100%;
  padding: 14px 16px;
  padding-right: ${({ $hasIcon }) => ($hasIcon ? '48px' : '16px')};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: ${({ theme }) => theme.fontSize.base};
  color: ${({ theme }) => theme.colors.text};
  outline: none;
  background: ${({ theme }) => theme.colors.bgWhite};

  &::placeholder {
    color: ${({ theme }) => theme.colors.textLight};
  }

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const EyeButton = styled.button`
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.textLight};
  display: flex;
  align-items: center;
  padding: 4px;
  cursor: pointer;
`;

const ForgotLink = styled(Link)`
  display: block;
  text-align: right;
  margin: ${({ theme }) => theme.spacing.sm} 0 ${({ theme }) => theme.spacing.xl};
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text};
  text-decoration: none;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 16px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  border: none;
  border-radius: ${({ theme }) => theme.radius.md};
  cursor: pointer;

  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  margin: ${({ theme }) => theme.spacing.xl} 0;
  color: ${({ theme }) => theme.colors.textLight};
  font-size: ${({ theme }) => theme.fontSize.sm};
  gap: ${({ theme }) => theme.spacing.md};

  &::before, &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${({ theme }) => theme.colors.border};
  }
`;

const SocialList = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing.md};
`;

const SocialBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: 14px;
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
  border: 1px solid transparent;
  transition: opacity 0.15s;

  ${({ $variant }) => {
    if ($variant === 'kakao') return `
      background: #FEE500;
      color: #000;
    `;
    if ($variant === 'google') return `
      background: #FFFFFF;
      color: #1F2937;
      border-color: #E5E7EB;
    `;
    if ($variant === 'apple') return `
      background: #000000;
      color: #FFFFFF;
    `;
    return '';
  }}

  &:hover { opacity: 0.9; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const SignupSection = styled.div`
  margin-top: ${({ theme }) => theme.spacing.xxl};
`;

const SignupTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${({ theme }) => theme.spacing.base};
`;

const SignupRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.md};
`;

const SignupBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: 14px 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
  border: none;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;

  &:hover { opacity: 0.9; }
`;

